import type {OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import {CachePreheatContextProvider} from '@github-ui/preheat-issues-manager/cache-preheat-context'
import {SidePanel} from '@github-ui/side-panel'
import {
  FocusTrapManagementContextProvider,
  useFocusTrapManagementContext,
} from '@github-ui/side-panel/FocusTrapManagementProvider'
import {StaleCacheBanner} from '@github-ui/stale-cache-banner'
import {testIdProps} from '@github-ui/test-id-props'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useLayoutEffect, useRef} from 'react'

import {useSiteHeaderHeight} from '../use-site-header-height'
import styles from './IssueSidePanel.module.css'

type IssueSidePanelProps = {
  onClose: () => void
  children: React.ReactNode
  optionConfig?: Partial<OptionConfig>
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

const IssueSidePanelInternal = ({onClose, children, optionConfig, returnFocusRef}: IssueSidePanelProps) => {
  const sidePanelReturnFocusRef = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    if (!returnFocusRef && !sidePanelReturnFocusRef.current) {
      sidePanelReturnFocusRef.current = document.activeElement as HTMLElement | null
    }
  }, [returnFocusRef])
  const sidePanelInitialFocusRef = useRef<HTMLDivElement>(null)
  const siteHeaderSize = useSiteHeaderHeight()
  const {disableFocusTrap} = useFocusTrapManagementContext()

  return (
    <SidePanel
      open
      defaultCloseElement="overlay"
      onClose={onClose}
      initialFocusRef={sidePanelInitialFocusRef}
      returnFocusRef={returnFocusRef ?? sidePanelReturnFocusRef}
      aria-labelledby="issue-side-panel"
      width="min(90%, 1280px)"
      topOffset={siteHeaderSize}
      externalFocusTrapDisabled={disableFocusTrap}
    >
      <GlobalCommands
        commands={{
          'issues-react:focus-next-issue': () => optionConfig?.onNavigateToNextItem?.(),
          'issues-react:focus-previous-issue': () => optionConfig?.onNavigateToPreviousItem?.(),
        }}
      />
      <div id="issue-viewer-side-panel" className={styles.IssueSidePanelContent}>
        <span
          tabIndex={-1}
          ref={sidePanelInitialFocusRef}
          {...testIdProps('side-panel-focus-target')}
          data-prevent-scroll-on-focus
        />
        <StaleCacheBanner />
        {children}
      </div>
    </SidePanel>
  )
}

export const IssueSidePanel = ({onClose, children, optionConfig, returnFocusRef}: IssueSidePanelProps) => {
  return (
    <FocusTrapManagementContextProvider>
      <CachePreheatContextProvider>
        <IssueSidePanelInternal onClose={onClose} optionConfig={optionConfig} returnFocusRef={returnFocusRef}>
          {children}
        </IssueSidePanelInternal>
      </CachePreheatContextProvider>
    </FocusTrapManagementContextProvider>
  )
}
